if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      requestGeneration = 0;
      quantityRequestGeneration = 0;
      selectionUnconfirmed = false;
      quantityRulesUnconfirmed = false;
      quantityUnavailable = false;
      quantityRestoreTimer = undefined;
      cancelledSelectionErrors = new WeakSet();
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.onProductSelect = (event) => {
          if (event.target !== this.variantSelectors || !event.promise?.catch) return;
          event.promise.catch((error) => {
            if (!this.cancelledSelectionErrors.has(error)) console.error(error);
          });
        };
        this.onQuantityInput = (event) => {
          if (event.target !== this.quantityInput) return;
          this.syncPurchaseState();
        };
        this.onPageShow = () => {
          clearTimeout(this.quantityRestoreTimer);
          // History restoration can set form values without input/change events.
          // Wait until that restoration finishes, then preserve the restored value.
          this.quantityRestoreTimer = setTimeout(() => {
            this.quantityRestoreTimer = undefined;
            if (!this.isConnected || !this.quantityInput) return;
            this.quantityInput.closest('quantity-input')?.validateQtyRules?.();
            this.syncPurchaseState();
          }, 0);
        };
        this.onGuardedSubmit = (event) => {
          if (event.target.closest('product-form') !== this.productForm || !this.purchaseBlocked()) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          if (!this.selectionUnconfirmed && !this.quantityRulesUnconfirmed) {
            this.quantityInput?.reportValidity?.();
          }
        };
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.nativeSubmitDisabled ??= this.productForm?.querySelector('[type="submit"]')?.hasAttribute('disabled') ?? true;
        this.addEventListener('input', this.onQuantityInput);
        this.addEventListener('change', this.onQuantityInput);
        this.addEventListener('shopify:product:select', this.onProductSelect);
        window.addEventListener('pageshow', this.onPageShow);
        // Native product forms use novalidate; block an unconfirmed/invalid purchase
        // before the target ProductForm listener, including programmatic submit.
        this.addEventListener('submit', this.onGuardedSubmit, true);

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        if (this.selectionUnconfirmed && this.lastVariantRequest) this.showRequestRecovery('variant');
        else if (this.quantityRulesUnconfirmed) this.showRequestRecovery('quantity');
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.requestGeneration += 1;
        this.quantityRequestGeneration += 1;
        this.cancelRequest(this.variantRequest);
        this.cancelRequest(this.quantityRequest);
        this.removeEventListener('input', this.onQuantityInput);
        this.removeEventListener('change', this.onQuantityInput);
        this.removeEventListener('shopify:product:select', this.onProductSelect);
        window.removeEventListener('pageshow', this.onPageShow);
        clearTimeout(this.quantityRestoreTimer);
        this.quantityRestoreTimer = undefined;
        this.removeEventListener('submit', this.onGuardedSubmit, true);
        this.removeAttribute('aria-busy');
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const currentProduct = new URL(this.dataset.url, window.location.href);
        const requestedProduct = new URL(productUrl, window.location.href);
        const shouldSwapProduct = currentProduct.origin !== requestedProduct.origin ||
          currentProduct.pathname !== requestedProduct.pathname;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        this.selectionUnconfirmed = true;
        this.syncPurchaseState();
        productForm?.handleErrorMessage?.();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id, sourceProductInfo.dataset.navigationUrl || '');

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      cancelRequest(context) {
        if (!context) return;
        const error = new Error('Product request superseded');
        error.name = 'AbortError';
        this.cancelledSelectionErrors.add(error);
        context.cancel?.(error);
        context.controller.abort();
        if (context === this.quantityRequest) {
          this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden');
        }
        this.rejectSelection(context, error);
      }

      rejectSelection(context, error) {
        if (context.selectionSettled || !context.selectionPromise) return;
        context.selectionSettled = true;
        // Native resolvePendingSelectPromise takes the deferred before a product
        // swap disconnects this owner. Do not reject that already-consumed event.
        if (context.selectionOwner?.pendingSelectPromise === null) return;
        if (context.selectionOwner?.pendingSelectPromise === context.selectionPromise) {
          context.selectionOwner.pendingSelectPromise = null;
        }
        context.selectionPromise.reject(error);
      }

      requestHTML(requestUrl, context) {
        return new Promise((resolve, reject) => {
          let settled = false;
          const finish = (error, html) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            context.cancel = null;
            context.requestSettled = true;
            if (error) reject(error);
            else resolve(html);
          };
          const timer = setTimeout(() => {
            const error = new Error('Product details took too long to load');
            error.code = 'PRODUCT_REQUEST_TIMEOUT';
            finish(error);
            context.controller.abort();
          }, 10000);
          context.cancel = (error) => finish(error);
          try {
            Promise.resolve(fetch(requestUrl, { signal: context.controller.signal }))
              .then((response) => {
                if (!response.ok) throw new Error('Product details could not be loaded');
                return response.text();
              })
              .then((text) => new DOMParser().parseFromString(text, 'text/html'))
              .then((html) => finish(null, html), (error) => finish(error));
          } catch (error) { finish(error); }
        });
      }

      isCurrentRequest(context, kind = 'variant') {
        if (!this.isConnected) return false;
        if (kind === 'quantity') {
          return context === this.quantityRequest && context.generation === this.quantityRequestGeneration &&
            context.variantGeneration === this.requestGeneration &&
            String(this.productForm?.variantIdInput?.value || '') === context.variantId;
        }
        return context === this.variantRequest && context.generation === this.requestGeneration;
      }

      validateProductHTML(html, expectedUrl) {
        const source = html.querySelector('product-info');
        if (!source) throw new Error('Product details are incomplete');
        if (expectedUrl && (!source.dataset.url ||
            new URL(source.dataset.url, window.location.href).pathname !== new URL(expectedUrl, window.location.href).pathname)) {
          throw new Error('Product details do not match this product');
        }
        const expectedSection = expectedUrl && new URL(expectedUrl, window.location.href).searchParams.get('section_id');
        if (!source.dataset.section || expectedSection && source.dataset.section !== expectedSection) {
          throw new Error('Product details do not match this section');
        }
        const sameProduct = new URL(source.dataset.url, window.location.href).pathname ===
          new URL(this.dataset.url, window.location.href).pathname;
        if (sameProduct) {
          for (const id of ['price', 'Inventory', 'Sku', 'Volume', 'Price-Per-Item']) {
            if (this.querySelector(`[id="${id}-${this.dataset.section}"]`) &&
                !source.querySelector(`[id="${id}-${source.dataset.section}"]`)) {
              throw new Error('Product information is incomplete');
            }
          }
        }
        const picker = source.querySelector('variant-selects');
        if (this.variantSelectors) {
          const script = picker?.querySelector('[data-selected-variant]');
          if (!script) throw new Error('Selected pack could not be confirmed');
          let variant;
          try { variant = JSON.parse(script.textContent); }
          catch { throw new Error('Selected pack could not be confirmed'); }
          if (variant !== null) {
            if (!variant || !/^[1-9]\d*$/.test(String(variant.id)) || typeof variant.available !== 'boolean') {
              throw new Error('Selected pack could not be confirmed');
            }
            if (this.productForm && String(source.querySelector('product-form input[name="id"]')?.value) !== String(variant.id)) {
              throw new Error('Selected pack does not match the purchase form');
            }
            if (variant.featured_media?.id && this.querySelector('media-gallery')) {
              const viewer = source.querySelector('media-gallery ul');
              const media = [...(viewer?.children || [])].filter((node) => node.matches('li[data-media-id]'));
              if (!media.some((node) => node.dataset.mediaId.endsWith(`-${variant.featured_media.id}`))) {
                throw new Error('Selected pack image is unavailable');
              }
            }
          }
        }
        if (this.productForm && !source.querySelector('product-form [type="submit"]')) {
          throw new Error('Product purchase details are incomplete');
        }
        if (this.quantityInput) {
          const quantity = source.querySelector('.product-form__quantity .quantity__input');
          if (!quantity || !html.getElementById(`Quantity-Form-${source.dataset.section}`)) {
            throw new Error('Product quantity details are incomplete');
          }
          if (!this.quantityRuleData(quantity).valid) throw new Error('Product quantity rules are incomplete');
        }
        return source;
      }

      showRequestRecovery(kind) {
        let notice = this.querySelector('[data-product-request-recovery]');
        if (!notice) {
          notice = document.createElement('div');
          notice.dataset.productRequestRecovery = '';
          const message = document.createElement('p');
          message.setAttribute('role', 'alert');
          const retry = document.createElement('button');
          retry.type = 'button';
          retry.className = 'button button--secondary';
          retry.addEventListener('click', () => {
            if (this.selectionUnconfirmed && this.lastVariantRequest) this.renderProductInfo(this.lastVariantRequest);
            else if (this.quantityRulesUnconfirmed) this.fetchQuantityRules();
          });
          notice.append(message, retry);
          (this.productForm || this).append(notice);
        }
        notice.hidden = false;
        notice.querySelector('p').textContent = kind === 'variant'
          ? 'This selection could not be confirmed. Retry its details before adding to cart.'
          : 'Quantity limits could not be confirmed. Retry before adding to cart.';
        notice.querySelector('button').textContent = kind === 'variant' ? 'Retry selected options' : 'Retry quantity limits';
        notice.querySelector('button').disabled = false;
      }

      clearRequestRecovery() {
        const notice = this.querySelector('[data-product-request-recovery]');
        if (notice) notice.hidden = true;
      }

      restoreRetryFocus(context, quantityReady = false) {
        if (!context.retryOrigin || !this.isConnected) return;
        const focus = document.activeElement;
        if (focus !== context.initiator && focus !== document.body && focus?.isConnected) return;
        const target = quantityReady
          ? (this.quantityInput?.disabled ? this.quantityForm : this.quantityInput)
          : this.querySelector('[data-product-request-recovery] button');
        if (target && !target.disabled) {
          if (target === this.quantityForm) target.setAttribute('tabindex', '-1');
          target.focus();
        }
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        if (!this.isConnected) return Promise.resolve(false);
        const current = this.variantRequest;
        if (current && this.isCurrentRequest(current) && !current.requestSettled && current.requestUrl === requestUrl &&
            this.variantSelectors?.pendingSelectPromise === current.selectionPromise) return current.promise;
        this.cancelRequest(this.variantRequest);
        this.cancelRequest(this.quantityRequest);
        const context = {
          generation: ++this.requestGeneration,
          requestUrl,
          controller: new AbortController(),
          selectionOwner: this.variantSelectors,
          selectionPromise: this.variantSelectors?.pendingSelectPromise,
          initiator: document.activeElement,
          retryOrigin: document.activeElement === this.querySelector('[data-product-request-recovery] button'),
        };
        this.variantRequest = context;
        this.abortController = context.controller;
        this.lastVariantRequest = { requestUrl, targetId, callback };
        this.selectionUnconfirmed = true;
        this.quantityRulesUnconfirmed = false;
        this.syncPurchaseState();
        this.setAttribute('aria-busy', 'true');
        this.querySelector('[data-product-request-recovery] button')?.setAttribute('disabled', '');
        context.promise = this.requestHTML(requestUrl, context)
          .then((html) => {
            if (!this.isCurrentRequest(context)) return false;
            this.validateProductHTML(html, requestUrl);
            callback(html);
            context.selectionSettled = true;
            // A product swap can disconnect this owner during a successful callback.
            if (!this.isCurrentRequest(context)) return true;
            this.pendingRequestUrl = null;
            this.selectionUnconfirmed = false;
            this.clearRequestRecovery();
            this.syncPurchaseState();
            const focus = document.activeElement;
            if (focus === context.initiator || focus === document.body || !focus?.isConnected) {
              this.querySelector(`[id="${targetId}"]`)?.focus();
            }
            return true;
          })
          .catch((error) => {
            this.rejectSelection(context, error);
            if (!this.isCurrentRequest(context)) return false;
            this.selectionUnconfirmed = true;
            this.showRequestRecovery('variant');
            this.restoreRetryFocus(context);
            this.syncPurchaseState();
            return false;
          })
          .finally(() => {
            if (this.isCurrentRequest(context)) this.removeAttribute('aria-busy');
          });
        return context.promise;
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const requestUrl = new URL(url, window.location.href);
        if (shouldFetchFullPage) requestUrl.searchParams.delete('section_id');
        else requestUrl.searchParams.set('section_id', this.sectionId);
        if (optionValues.length) {
          requestUrl.searchParams.delete('variant');
          requestUrl.searchParams.set('option_values', optionValues.join(','));
        }
        return `${requestUrl.pathname}${requestUrl.search}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.nativeSubmitDisabled = html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true;
          this.selectionUnconfirmed = false;
          this.quantityRulesUnconfirmed = false;
          this.syncPurchaseState(this.nativeSubmitDisabled ? window.variantStrings.soldOut : undefined);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId, navigationUrl = this.dataset.navigationUrl) {
        const productUrl = new URL(url, window.location.href);
        productUrl.searchParams.delete('section_id');
        productUrl.searchParams.delete('option_values');
        if (variantId) productUrl.searchParams.set('variant', variantId);
        else productUrl.searchParams.delete('variant');
        this.querySelector('share-button')?.updateUrl(productUrl.href);

        const navigation = new URL(productUrl.href);
        if (navigationUrl) {
          try {
            const context = new URL(navigationUrl, window.location.href);
            const nativePath = productUrl.pathname.match(/^(.*)\/products\/([^/]+)\/?$/);
            const contextPath = context.pathname.match(/^(.*)\/collections\/([^/]+)\/products\/([^/]+)\/?$/);
            const sameCollectionProduct = nativePath && contextPath && contextPath[1] === nativePath[1] &&
              contextPath[2] !== 'all' && contextPath[3] === nativePath[2];
            if (context.origin === productUrl.origin &&
                (context.pathname === productUrl.pathname || sameCollectionProduct)) {
              navigation.pathname = context.pathname;
              for (const [key, value] of context.searchParams) {
                if (!['variant', 'section_id', 'option_values'].includes(key) && !navigation.searchParams.has(key)) {
                  navigation.searchParams.append(key, value);
                }
              }
            }
          } catch {
            // An invalid optional navigation context never blocks native selection.
          }
        }
        this.querySelectorAll('a.product__title, a.product__view-details').forEach((link) => {
          link.href = `${navigation.pathname}${navigation.search}${navigation.hash}`;
        });
        if (this.dataset.updateUrl === 'false') return;
        const current = new URL(window.location.href);
        const sameProduct = productUrl.pathname === new URL(this.dataset.url, window.location.href).pathname;
        if (sameProduct && current.origin === navigation.origin &&
            (current.pathname === navigation.pathname || current.pathname === productUrl.pathname)) {
          for (const [key, value] of current.searchParams) {
            if (!['variant', 'section_id', 'option_values'].includes(key) && !navigation.searchParams.has(key)) {
              navigation.searchParams.append(key, value);
            }
          }
          if (!navigation.hash) navigation.hash = current.hash;
        }
        window.history.replaceState(window.history.state, '', `${navigation.pathname}${navigation.search}${navigation.hash}`);
      }

      setUnavailable() {
        this.nativeSubmitDisabled = true;
        this.selectionUnconfirmed = false;
        this.syncPurchaseState(window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);
        // Native quick-add owns a cloned section namespace. Keep fetched viewer
        // IDs paired with its existing thumbnail targets and separate from the PDP.
        if (this.dataset.originalSection && mediaGalleryDestination) {
          mediaGalleryDestination.innerHTML = mediaGalleryDestination.innerHTML.replaceAll(this.sectionId, this.dataset.section);
        }

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      quantityIsValid() {
        if (!this.quantityInput) return true;
        const value = Number(this.quantityInput.value);
        const min = Number(this.quantityInput.min || 1);
        const max = this.quantityInput.hasAttribute('max') ? Number(this.quantityInput.max) : null;
        const step = Number(this.quantityInput.step || 1);
        return Number.isSafeInteger(value) && value >= min && value > 0 &&
          (max === null || value <= max) && (value - min) % step === 0;
      }

      purchaseBlocked() {
        return this.nativeSubmitDisabled || this.selectionUnconfirmed || this.quantityRulesUnconfirmed ||
          this.quantityUnavailable || !this.quantityIsValid();
      }

      syncPurchaseState(text) {
        const blocked = this.purchaseBlocked();
        const form = this.productForm;
        if (form?.toggleSubmitButton) form.toggleSubmitButton(blocked, text);
        else form?.querySelector('[type="submit"]')?.toggleAttribute('disabled', blocked);
        const payment = this.querySelector('.shopify-payment-button');
        if (payment) {
          this.originalPaymentHidden ??= payment.hidden;
          payment.hidden = this.originalPaymentHidden || blocked;
        }
        if (!this.quantityInput) return;
        this.quantityInput.disabled = this.selectionUnconfirmed || this.quantityRulesUnconfirmed || this.quantityUnavailable;
        const invalid = !this.quantityIsValid();
        this.quantityInput.setAttribute('aria-invalid', String(invalid));
        let message = this.quantityForm?.querySelector('[data-quantity-limit-message]');
        if (!message && this.quantityForm) {
          message = document.createElement('p');
          message.dataset.quantityLimitMessage = '';
          message.setAttribute('role', 'status');
          this.quantityForm.append(message);
        }
        if (message) {
          message.hidden = !this.quantityUnavailable && !invalid;
          message.textContent = this.quantityUnavailable
            ? 'This pack cannot be added within its current quantity limits. Review your cart or choose another pack.'
            : 'Choose a quantity within this pack’s limits.';
        }
        const value = Number(this.quantityInput.value);
        this.quantityForm?.querySelectorAll('.quantity__button').forEach((button) => {
          const atLimit = button.name === 'minus' ? value <= Number(this.quantityInput.min) :
            this.quantityInput.hasAttribute('max') && value >= Number(this.quantityInput.max);
          button.disabled = this.quantityInput.disabled || atLimit;
        });
      }

      quantityRuleData(input) {
        const cartQuantity = Number(input.dataset.cartQuantity || 0);
        const min = Number(input.dataset.min || 1);
        const max = input.dataset.max === undefined || input.dataset.max === '' ? null : Number(input.dataset.max);
        const step = Number(input.step || 1);
        const valid = [cartQuantity, min, step].every(Number.isSafeInteger) && cartQuantity >= 0 && min >= 1 && step >= 1 &&
          (max === null || Number.isSafeInteger(max) && max >= 0);
        return { cartQuantity, min, max, step, valid };
      }

      setQuantityBoundries({ preserveValue = false } = {}) {
        if (!this.quantityInput) return;
        const input = this.quantityInput;
        const data = this.quantityRuleData(input);
        const min = data.valid ? (data.cartQuantity >= data.min ? Math.min(data.min, data.step) : data.min) : 1;
        const max = data.max === null ? null : Math.max(0, data.max - data.cartQuantity);
        const step = data.valid ? data.step : 1;
        this.quantityUnavailable = !data.valid || max !== null && max < min;
        input.min = min;
        if (max === null) input.removeAttribute('max');
        else input.max = max;
        const previous = Number(input.value);
        if (!preserveValue || !Number.isSafeInteger(previous) || previous < min ||
            max !== null && previous > max || (previous - min) % step !== 0) input.value = min;
        this.syncPurchaseState();
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = String(this.productForm?.variantIdInput?.value || '');
        if (!currentVariantId || !this.isConnected || this.selectionUnconfirmed) return Promise.resolve(false);
        if (this.quantityRequest && this.isCurrentRequest(this.quantityRequest, 'quantity') &&
            !this.quantityRequest.requestSettled) return this.quantityRequest.promise;
        this.cancelRequest(this.quantityRequest);
        const context = {
          generation: ++this.quantityRequestGeneration,
          variantGeneration: this.requestGeneration,
          variantId: currentVariantId,
          controller: new AbortController(),
          initiator: document.activeElement,
          retryOrigin: document.activeElement === this.querySelector('[data-product-request-recovery] button'),
        };
        this.quantityRequest = context;
        this.quantityRulesUnconfirmed = true;
        this.syncPurchaseState();
        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        spinner?.classList.remove('hidden');
        this.querySelector('[data-product-request-recovery] button')?.setAttribute('disabled', '');
        const requestUrl = new URL(this.buildRequestUrlWithParams(this.dataset.url, []), window.location.href);
        requestUrl.searchParams.set('variant', currentVariantId);
        context.promise = this.requestHTML(`${requestUrl.pathname}${requestUrl.search}`, context)
          .then((html) => {
            if (!this.isCurrentRequest(context, 'quantity')) return false;
            const source = this.validateProductHTML(html, this.buildRequestUrlWithParams(this.dataset.url, []));
            if (String(source.querySelector('product-form input[name="id"]')?.value) !== currentVariantId) {
              throw new Error('Quantity limits do not match the selected pack');
            }
            this.updateQuantityRules(this.dataset.section, html, { preserveValue: true });
            this.nativeSubmitDisabled = source.querySelector('product-form [type="submit"]')?.hasAttribute('disabled') ?? true;
            this.quantityRulesUnconfirmed = false;
            this.clearRequestRecovery();
            this.syncPurchaseState(this.nativeSubmitDisabled ? window.variantStrings.soldOut : undefined);
            this.restoreRetryFocus(context, true);
            return true;
          })
          .catch(() => {
            if (!this.isCurrentRequest(context, 'quantity')) return false;
            this.quantityRulesUnconfirmed = true;
            this.showRequestRecovery('quantity');
            this.syncPurchaseState();
            this.restoreRetryFocus(context);
            return false;
          })
          .finally(() => {
            if (this.isCurrentRequest(context, 'quantity')) spinner?.classList.add('hidden');
          });
        return context.promise;
      }

      updateQuantityRules(sectionId, html, { preserveValue = false } = {}) {
        if (!this.quantityInput) return;
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) throw new Error('Product quantity details are incomplete');
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
        this.setQuantityBoundries({ preserveValue });
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
